import {
    ChangeDetectorRef,
    Component,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs/operators';

import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import { SearchUserContainerComponent } from '../../user/search-user.container.component';
import { User } from '../../user/user.model';
import { CmpsCardComponent } from '../card';
import { useSelector } from '../store-use-selector';

import { TranslationKeyMap } from './main.i18n';
import { mainReducer } from './main.reducer';
import { MainState } from './main.state';
import { reload } from './state/reload.action';
import { reset } from './state/reset.action';
import { selectTemplateVersion } from './state/select-template-version.action';
import { selectUser } from './state/select-user.action';

@Component({
    selector: 'cmps-eval-main',
    templateUrl: './main.component.html',
    host: {
        class: 'cmps-eval-main',
    },
})
@TakeUntilDestroy()
@UseStore<MainState>('evaluation.main', mainReducer)
export class MainComponent
    implements StateToProps<MainState>, TakeUntilDestroy
{
    state: MainState;

    @ViewChildren(CmpsCardComponent)
    private readonly _cards: QueryList<CmpsCardComponent>;

    @ViewChild('userSearchCard', { read: CmpsCardComponent })
    private readonly _userSearchCard: CmpsCardComponent;

    @ViewChild('templateVersionCard', { read: CmpsCardComponent })
    private readonly _templateVersionCard: CmpsCardComponent;

    @ViewChild('userSearch', { read: SearchUserContainerComponent })
    private readonly _userSearch: SearchUserContainerComponent;

    /** @private */
    _i18n = TranslationKeyMap;

    constructor(
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _router: Router,
        private readonly _changeDetectorRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        useSelector<{
            value?: User;
            initialLoad?: boolean;
        }>('evaluation.main.selectedUser', () => null)
            .pipe(
                // skip(1), // ignore initial value when connecting to store
                tap((state) => {
                    this._changeDetectorRef.detectChanges();
                    if (state?.value == null) {
                        this._userSearchCard?.expand();
                        this._templateVersionCard?.expand();
                        if (state != null && !state.initialLoad) {
                            // this._router.navigate(['evaluation']);
                        }
                    } else {
                        this._userSearchCard?.collapse();
                        if (state != null && !state.initialLoad) {
                            // eslint-disable-next-line @typescript-eslint/no-floating-promises
                            this._router.navigate(['evaluation'], {
                                queryParams: { userId: state?.value?.id },
                            });
                        }
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        useSelector<{
            value?: number;
            initialLoad?: boolean;
        }>('evaluation.main.selectedTemplateVersionId', () => null)
            .pipe(
                // skip(1), // ignore initial value when connecting to store
                tap((state) => {
                    this._changeDetectorRef.detectChanges();
                    if (state?.value == null) {
                        this._templateVersionCard?.expand();
                        if (state != null && !state.initialLoad) {
                            // this._router.navigate(['evaluation']);
                        }
                    } else {
                        this._cards?.forEach((e) => e.collapse());
                        if (state != null && !state.initialLoad) {
                            // eslint-disable-next-line @typescript-eslint/no-floating-promises
                            this._router.navigate(['evaluation', 'drafts'], {
                                queryParams: {
                                    userId: this.state?.selectedUser?.value?.id,
                                    templateVersionId: state.value,
                                },
                            });
                        }
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._activatedRoute.queryParamMap
            .pipe(
                tap((queryParams) => {
                    let userId = parseInt(queryParams.get('userId'));
                    userId = !isNaN(userId) ? userId : null;
                    let user: User;
                    if (userId != null) {
                        user = new User();
                        user.id = userId;
                    }

                    let templateVersionId = parseInt(
                        queryParams.get('templateVersionId'),
                    );
                    templateVersionId = !isNaN(templateVersionId)
                        ? templateVersionId
                        : null;

                    const sameUserId =
                        userId != null &&
                        userId === this.state?.selectedUser?.value?.id;
                    const sameTemplateVersionId =
                        templateVersionId != null &&
                        templateVersionId ===
                            this.state?.selectedTemplateVersionId?.value;
                    if (sameUserId && sameTemplateVersionId) {
                        return;
                    }

                    if (!this.state?.didInitialLoad) {
                        useDispatcher(
                            this,
                            reload({ user, templateVersionId }),
                        );
                    } else {
                        if (!sameUserId) {
                            useDispatcher(this, selectUser({ user }));
                        }
                        if (!sameTemplateVersionId) {
                            useDispatcher(
                                this,
                                selectTemplateVersion({ templateVersionId }),
                            );
                        }
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        useDispatcher(this, reset({}));
    }

    /** @private */
    _onUserSelect(user: User): void {
        useDispatcher(this, selectUser({ user }));
    }

    /** @private */
    _onSelectTemplateVersionClick(templateVersionId: string): void {
        // TODO revise. calls private method
        this._userSearch?._onSelectClick();

        let intId = parseInt(templateVersionId);
        intId = !isNaN(intId) ? intId : null;
        if (intId == null) {
            return;
        }

        useDispatcher(
            this,
            selectTemplateVersion({ templateVersionId: intId }),
        );
    }
}
